export class GameLoop {
  private isRunning: boolean = false;
  private lastFrameTime: number = 0;
  private lastRenderTime: number = 0;
  private accumulator: number = 0;
  private animationFrameId: number | null = null;

  private readonly fixedTimeStep: number;
  private readonly maxAccumulator: number;
  private readonly targetRenderFPS: number = 60;
  private readonly minRenderInterval: number;

  private physicsCallback: ((deltaTime: number) => void) | null = null;
  private renderCallback: ((deltaTime: number) => void) | null = null;

  private physicsFrameCount: number = 0;
  private renderFrameCount: number = 0;
  private lastFPSUpdate: number = 0;
  private physicsFPS: number = 0;
  private renderFPS: number = 0;

  constructor(updateRate: number = 60) {
    this.fixedTimeStep = 1 / updateRate;
    this.maxAccumulator = 0.25;
    this.minRenderInterval = 1 / this.targetRenderFPS;
  }

  public start(
    physicsCallback: (deltaTime: number) => void,
    renderCallback: (deltaTime: number) => void
  ): void {
    if (this.isRunning) {
      return;
    }

    this.isRunning = true;
    this.physicsCallback = physicsCallback;
    this.renderCallback = renderCallback;

    const currentTime = performance.now() / 1000;
    this.lastFrameTime = currentTime;
    this.lastRenderTime = currentTime;
    this.lastFPSUpdate = currentTime;
    this.accumulator = 0;
    this.physicsFrameCount = 0;
    this.renderFrameCount = 0;

    this.animationFrameId = requestAnimationFrame(this.tick);
  }

  public stop(): void {
    if (!this.isRunning) {
      return;
    }

    this.isRunning = false;
    if (this.animationFrameId !== null) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }
  }

  public isActive(): boolean {
    return this.isRunning;
  }

  private tick = (timestamp: number): void => {
    if (!this.isRunning) {
      return;
    }

    const currentTime = timestamp / 1000;
    let deltaTime = currentTime - this.lastFrameTime;

    if (deltaTime > this.maxAccumulator) {
      deltaTime = this.maxAccumulator;
    }

    this.lastFrameTime = currentTime;
    this.accumulator += deltaTime;

    while (this.accumulator >= this.fixedTimeStep) {
      if (this.physicsCallback) {
        this.physicsCallback(this.fixedTimeStep);
        this.physicsFrameCount++;
      }
      this.accumulator -= this.fixedTimeStep;
    }

    const timeSinceLastRender = currentTime - this.lastRenderTime;
    if (timeSinceLastRender >= this.minRenderInterval) {
      if (this.renderCallback) {
        this.renderCallback(deltaTime);
        this.renderFrameCount++;
      }
      this.lastRenderTime = currentTime;
    }

    if (currentTime - this.lastFPSUpdate >= 1.0) {
      this.physicsFPS = this.physicsFrameCount;
      this.renderFPS = this.renderFrameCount;
      this.physicsFrameCount = 0;
      this.renderFrameCount = 0;
      this.lastFPSUpdate = currentTime;
    }

    this.animationFrameId = requestAnimationFrame(this.tick);
  };

  public getPhysicsFPS(): number {
    return this.physicsFPS;
  }

  public getRenderFPS(): number {
    return this.renderFPS;
  }
}
